/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  AVATAR_SIZE_EXTRA_SMALL,
  Avatar,
  AvatarProps,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  ButtonProps,
  DROPDOWN_POSITION_RIGHT,
  Dropdown,
  DropdownItemData,
  DropdownMenuProps,
  DropdownSectionData,
  ICON_HELP,
  NavigationItem,
  NavigationSection,
  ToolbarProps,
  UserProfile,
} from '@squiz/sds';
import { useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CapabilitySwitcher } from '../../Components/CapabilitySwitcher/CapabilitySwitcher';
import { TOOLBAR_DATA_IDS, TOOLBAR_DATA_ID_PREFIX } from '../../Constants/DataIds.constants';
import { DXP_MENU_RESOURCES_SECTION, DXP_MENU_USER_ACCOUNT_SECTION } from '../../Constants/NavSections.constants';
import { CORE_STRINGS } from '../../Constants/Strings.constants';
import { DxpTenantType, EntityDxpUserMe, EntityDxpUserMeOrganisation } from '../../Services/console';
import { useAccountOptions } from '../UseAccountOptions/UseAccountOptions.hook';

/**
 * React hook to configure the Toolbar component.
 */
export function useToolbarConfiguration(props: ToolbarProps): ToolbarProps {
  const { t } = useTranslation();
  const toolbarProps = props;

  const { consoleUser, tenant }: { consoleUser: EntityDxpUserMe | null; tenant: EntityDxpUserMeOrganisation | null } =
    useConsoleServiceStore();

  const dxpAccountMenu = DXP_MENU_USER_ACCOUNT_SECTION;
  dxpAccountMenu.items = useAccountOptions();

  // Foundations for any toolbar buttons
  const toolbarButtonProps: ButtonProps = {
    format: BUTTON_FORMAT_TRANSPARENT,
    theme: BUTTON_THEME_DEFAULT,
  };

  // We only want to show a user when we have data
  if (consoleUser) {
    const userProfile: UserProfile = {
      email: consoleUser.email,
      firstName: consoleUser.givenName,
      lastName: consoleUser.familyName,
    };

    // Set up avatar dropdown menu
    const avatarProps: AvatarProps = {
      avatarSize: AVATAR_SIZE_EXTRA_SMALL,
      user: userProfile,
    };
    const dxpUser = <Avatar {...avatarProps} />;

    const userPrefixDataID = TOOLBAR_DATA_ID_PREFIX.accountMenu;
    const userMenu: DropdownMenuProps = {
      buttonProps: {
        ...toolbarButtonProps,
        customContent: dxpUser,
        label: `Toggle account menu`,
      },
      dropdownPosition: DROPDOWN_POSITION_RIGHT,
      heading: t(CORE_STRINGS.account),
      sections: [getDropdownMenuSection(dxpAccountMenu, userPrefixDataID, t)],
    };

    // Set up help dropdown menu
    const helpPrefixDataID = TOOLBAR_DATA_ID_PREFIX.helpMenu;
    const helpMenu: DropdownMenuProps = {
      buttonProps: {
        ...toolbarButtonProps,
        icon: ICON_HELP,
        label: `Toggle help menu`,
      },
      classes: `sds-toolbar-menus--help`,
      // @ts-ignore
      'data-id': TOOLBAR_DATA_IDS.helpDropdownMenu,
      dropdownPosition: DROPDOWN_POSITION_RIGHT,
      heading: t(CORE_STRINGS.resources),
      key: `dxp-help-menu`,
      sections: [getDropdownMenuSection(DXP_MENU_RESOURCES_SECTION, helpPrefixDataID, t)],
    };

    // Put it together
    toolbarProps.toolbarMenus = [helpMenu];
    toolbarProps.toolbarUser = <Dropdown {...userMenu} data-id={TOOLBAR_DATA_IDS.userAccountDropdownMenu} />;

    if (!toolbarProps.text) {
      toolbarProps.text = `Squiz DXP`;
    }

    if (tenant && tenant.tenantType === DxpTenantType.organisation) {
      toolbarProps.switcher = <CapabilitySwitcher />;
    } else {
      toolbarProps.switcher = null;
    }
  }

  return toolbarProps;
}

const getDropdownMenuSection = (
  navigationSection: NavigationSection,
  dataPrefix: string,
  t: TFunction,
): DropdownSectionData => {
  const dropdownItems = navigationSection.items.map((item: NavigationItem): DropdownItemData => {
    const itemText = t(item.label);
    const dataID = `${dataPrefix}: ${itemText}`;

    return {
      action: item.action,
      dataID,
      key: item.key,
      label: itemText,
    } as DropdownItemData;
  });

  return {
    items: dropdownItems.map((item) => {
      return {
        ...item,
        label: t(item.label as string),
      };
    }),
    key: navigationSection.key,
  };
};
